// src/components/AudioPlayer.js
import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useTimingRows } from '../audio/useTimings';
import { rowIs, hasVerse, verseStart, verseEnd, verseAt } from '../audio/timings';
import { BOOK_NAMES } from '../utils/bibleBooks';

// Chapters that run on past the portion only stop this close to the verse
// boundary; anything later means the reader dragged past it on purpose.
const STOP_WINDOW = 2;

// `bounds` (portionBounds) and `timingLang` (timingLangFor) are optional. With
// both, and once verse timings exist for the recordings, the first chapter
// starts at the portion's first verse and the last stops after its last
// verse, which counts as the end for onFinished. Without them, or without
// timings, every chapter plays whole exactly as before.
// The ref offers canPlayFrom(book, chapter, verse) and playFrom(…) for
// "listen from here". `onTimings` is called when timings arrive for more of the
// chapters, so a page can start offering "play from verse" where it now can.
// `chapterLabels` (one per chapter) names the chapter buttons for a portion
// that crosses books ("1 Kings Ch. 22", "2 Kings Ch. 1"), which startChap
// counting alone cannot.
// `onVerseChange({ book, chapter, verse } | null)` reports the verse being read
// whenever it changes (null during a chapter title, after the portion ends,
// and when the player goes away), and `onPlayingChange(bool)` reports play and
// pause, so a page can light up and follow that verse in its text.
const AudioPlayer = forwardRef(function AudioPlayer(
  { label, book, audioJson, startChap = 1, lang = 'en', onFinished, bounds = null, timingLang = null, chapterLabels = null, onTimings = null,
    onVerseChange = null, onPlayingChange = null }, ref) {
  const urls = React.useMemo(() => {
    if (!audioJson) return [];
    try {
      const parsed = JSON.parse(audioJson);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return [audioJson];
    }
  }, [audioJson]);

  const [activeIdx, setActiveIdx] = useState(0);
  const audioRef = useRef(null);
  // Set just before activeIdx changes, to carry playback across the source swap.
  const resumeRef = useRef(false);

  const rows = useTimingRows(urls, timingLang);
  // Where to seek once the current source can take it (seconds), or null.
  const pendingSeekRef = useRef(null);
  // Whether this player has been started, so late timings never yank a
  // recording the reader is already listening to.
  const startedRef = useRef(false);
  // For spotting playback running across the portion's end; a seek resets it.
  const lastTimeRef = useRef(0);
  const seekedRef = useRef(false);
  const stopTimerRef = useRef(null);
  const [verseNow, setVerseNow] = useState(null);
  // Set once the last chapter has finished, so the page's verse marker goes
  // while the header can still name the verse that ended; cleared on play.
  const [finished, setFinished] = useState(false);

  const last = urls.length - 1;

  const timedCount = rows.filter(Boolean).length;
  const onTimingsRef = useRef(onTimings);
  onTimingsRef.current = onTimings;
  useEffect(() => { if (timedCount) onTimingsRef.current?.(); }, [timedCount]);

  // ── Telling the page which verse is playing ──────────────────────────────
  const onVerseChangeRef = useRef(onVerseChange);
  onVerseChangeRef.current = onVerseChange;
  const onPlayingChangeRef = useRef(onPlayingChange);
  onPlayingChangeRef.current = onPlayingChange;
  const nowRow = rows[activeIdx];
  const nowBook = nowRow && verseNow != null && !finished ? nowRow.book : null;
  const nowChapter = nowBook ? nowRow.chapter : null;
  const nowVerse = nowBook ? verseNow : null;
  // Primitive deps, so the page hears only of real changes, not of every
  // timeupdate (about four a second).
  useEffect(() => {
    onVerseChangeRef.current?.(nowBook ? { book: nowBook, chapter: nowChapter, verse: nowVerse } : null);
  }, [nowBook, nowChapter, nowVerse]);
  useEffect(() => () => {
    onVerseChangeRef.current?.(null);
    onPlayingChangeRef.current?.(false);
  }, []);

  // The portion's first verse, if chapter `idx` is where it begins.
  function startFor(idx) {
    const row = rows[idx];
    if (idx !== 0 || !bounds || !rowIs(row, bounds.first.book, bounds.first.chapter)) return 0;
    return verseStart(row, bounds.first.verse) || 0;
  }
  // The end of the portion's last verse, if chapter `idx` runs on past it.
  function stopFor(idx) {
    const row = rows[idx];
    if (idx !== last || !bounds || !rowIs(row, bounds.last.book, bounds.last.chapter)) return null;
    const end = verseEnd(row, bounds.last.verse);
    // Ending with the chapter: let the recording end by itself.
    return end != null && end < row.duration - 0.5 ? end : null;
  }

  function applyPendingSeek() {
    const el = audioRef.current;
    if (!el || pendingSeekRef.current == null || el.readyState < 1) return;
    const t = pendingSeekRef.current;
    pendingSeekRef.current = null;
    seekedRef.current = true;
    try { el.currentTime = t; } catch { /* not seekable; plays from the top */ }
  }

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.load();
    if (resumeRef.current) {
      resumeRef.current = false;
      // Rejects if the browser blocks playback or the source changes again mid-load.
      el.play().catch(() => {});
    }
  }, [activeIdx]);

  // Reset to first chapter when audio source changes (language switch)
  useEffect(() => {
    resumeRef.current = false;
    pendingSeekRef.current = null;
    startedRef.current = false;
    setActiveIdx(0);
  }, [audioJson]);

  // Timings usually arrive after the player is drawn: point the untouched
  // first chapter at the portion's first verse.
  const firstStart = startFor(0);
  useEffect(() => {
    if (!firstStart || startedRef.current || activeIdx !== 0) return;
    pendingSeekRef.current = firstStart;
    applyPendingSeek();
  }, [firstStart]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(stopTimerRef.current), []);
  useEffect(() => { clearTimeout(stopTimerRef.current); setVerseNow(null); }, [activeIdx, audioJson]);

  // Chain to the next chapter when one finishes; after the last, the reading is done.
  function handleEnded() {
    clearTimeout(stopTimerRef.current);
    if (activeIdx >= urls.length - 1) {
      setFinished(true);
      onPlayingChangeRef.current?.(false);
      onFinished?.();
      return;
    }
    pendingSeekRef.current = startFor(activeIdx + 1) || null;
    resumeRef.current = true;
    setActiveIdx(activeIdx + 1);
  }

  // The portion ends partway through this chapter: stop there, as if it ended.
  function reachStop(el) {
    clearTimeout(stopTimerRef.current);
    el.pause();
    lastTimeRef.current = el.currentTime;
    // The next verse may have begun by a fraction; show the one that ended.
    if (bounds) setVerseNow(bounds.last.verse);
    handleEnded();
  }

  function handleTimeUpdate(e) {
    const el = e.currentTarget;
    const t = el.currentTime;
    const prev = lastTimeRef.current;
    lastTimeRef.current = t;
    const row = rows[activeIdx];
    setVerseNow(row ? verseAt(row, t) : null);
    if (seekedRef.current) { seekedRef.current = false; return; }
    const stop = stopFor(activeIdx);
    if (stop == null || el.paused) return;
    if (prev < stop && t >= stop && t - stop < STOP_WINDOW) { reachStop(el); return; }
    // timeupdate comes only every quarter second or so, which can let the
    // next verse's first word slip out; a timer lands closer.
    if (t < stop && stop - t < 1) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = setTimeout(() => {
        const now = el.currentTime;
        if (!el.paused && now >= stop - 0.1 && now - stop < STOP_WINDOW) reachStop(el);
      }, ((stop - t) / (el.playbackRate || 1)) * 1000);
    }
  }

  function handleSeeking() {
    seekedRef.current = true;
    clearTimeout(stopTimerRef.current);
  }

  // A chapter that ends pauses on its way to the next one; that is not the
  // reader pausing, so the page keeps following. The last chapter's end is
  // reported by handleEnded.
  function handlePause(e) {
    if (!e.currentTarget.ended) onPlayingChangeRef.current?.(false);
  }

  function handlePlay() {
    startedRef.current = true;
    setFinished(false);
    onPlayingChangeRef.current?.(true);
    applyPendingSeek();
  }

  // Switching chapters mid-playback keeps playing; switching while paused just loads.
  // The portion's first chapter starts at the portion's first verse.
  function selectChapter(idx) {
    const el = audioRef.current;
    const start = startFor(idx);
    if (idx === activeIdx) {
      if (start && el) { pendingSeekRef.current = start; applyPendingSeek(); }
      return;
    }
    pendingSeekRef.current = start || null;
    resumeRef.current = !!(el && !el.paused && !el.ended);
    setActiveIdx(idx);
  }

  // ── Listen from here ─────────────────────────────────────────────────────
  function chapterWith(b, c, v) {
    return rows.findIndex(row => rowIs(row, b, c) && hasVerse(row, v));
  }
  useImperativeHandle(ref, () => ({
    canPlayFrom: (b, c, v) => chapterWith(b, c, v) >= 0,
    playFrom(b, c, v) {
      const idx = chapterWith(b, c, v);
      if (idx < 0) return false;
      const el = audioRef.current;
      startedRef.current = true;
      pendingSeekRef.current = verseStart(rows[idx], v) || 0;
      if (idx === activeIdx) {
        if (el) {
          // A source not loaded yet takes the seek on loadedmetadata.
          applyPendingSeek();
          el.play().catch(() => {});
        }
      } else {
        resumeRef.current = true;
        setActiveIdx(idx);
      }
      return true;
    },
  }));

  if (urls.length === 0) return null;

  // Chapter button label based on language
  function chapterLabel(idx) {
    if (chapterLabels?.[idx]) return chapterLabels[idx];
    const chapNum = startChap + idx;
    if (lang === 'zh' || lang === 'sc') return `${book} 第${chapNum}章`;
    if (lang === 'es') return `${book} Cap. ${chapNum}`;
    return `${book} Ch. ${chapNum}`;
  }

  // Single chapter label shown in header
  function singleLabel() {
    const chapNum = startChap;
    if (lang === 'zh' || lang === 'sc') return `${book} 第${chapNum}章`;
    if (lang === 'es') return `${book} Cap. ${chapNum}`;
    return `${book.toUpperCase()} CH. ${chapNum}`;
  }

  // "Colossians 3:12" while a verse with known timing is playing. The second
  // book of a cross-book portion is named from the book list.
  const row = rows[activeIdx];
  let verseLabel = null;
  if (row && verseNow != null) {
    const name = !bounds || row.book === bounds.first.book
      ? book
      : (BOOK_NAMES[row.book]?.[lang] || BOOK_NAMES[row.book]?.en || row.book);
    verseLabel = `${name} ${row.chapter}:${verseNow}`;
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <span style={styles.icon}>🎵</span>
        <span style={styles.labelText}>{label.toUpperCase()}</span>
        {verseLabel
          ? <span style={styles.verseNow} aria-live="off">{verseLabel}</span>
          : urls.length === 1 && <span style={styles.chapRight}>{singleLabel()}</span>}
      </div>

      <audio ref={audioRef} controls style={styles.audio} src={urls[activeIdx]} onEnded={handleEnded}
        onLoadedMetadata={applyPendingSeek} onPlay={handlePlay} onSeeking={handleSeeking}
        onPause={handlePause}
        onTimeUpdate={handleTimeUpdate} />

      {urls.length > 1 && (
        <div style={styles.chapRow}>
          {urls.map((_, idx) => (
            <button
              key={idx}
              onClick={() => selectChapter(idx)}
              style={{ ...styles.chapBtn, ...(idx === activeIdx ? styles.chapBtnActive : {}) }}
            >
              {chapterLabel(idx)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default AudioPlayer;

const styles = {
  wrapper: { background: 'var(--surface)', borderRadius: '8px', padding: '12px 14px', marginBottom: '10px', border: '1px solid var(--border)' },
  header: { display: 'flex', alignItems: 'center', marginBottom: '10px', gap: '8px' },
  icon: { fontSize: '14px' },
  labelText: { color: 'var(--text-sec)', fontSize: '12px', fontWeight: '600', letterSpacing: '0.05em', flex: 1 },
  chapRight: { color: '#00d4aa', fontSize: '12px', fontWeight: '600' },
  verseNow: { color: 'var(--text-sec)', fontSize: '12px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '55%' },
  audio: { width: '100%', height: '36px', borderRadius: '20px', outline: 'none' },
  chapRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' },
  chapBtn: { padding: '4px 14px', borderRadius: '20px', border: 'none', background: 'var(--border)', color: 'var(--text)', fontSize: '13px', cursor: 'pointer', transition: 'all 0.15s' },
  chapBtnActive: { background: '#00d4aa', color: '#0a0a1a', fontWeight: '700' },
};
